import { useState, useEffect, useRef } from 'react'
import ChartMenu, { MenuRow, MenuSwatch, MenuSelect, MenuNumber, MenuToggle, MenuDivider, MenuActions, MenuButton } from './menu/ChartMenu'

// ============================================
// DVPSettingsMenu — Delta Volume Pro's toolbar.
//
// Frame, fit, outside-click/Esc and styles: menu/ChartMenu + the CHART MENUS
// block in styles.css.
//
// Layout: one row of display controls (bull/bear column colors + mode +
// units), one row of the maths (rolling length, smoothing, sigma lookback),
// one row of band toggles, then HIDE.
//
// AUTO-APPLY (no Apply button): toggles, colours and dropdowns commit on the
// spot; the number fields commit ~200ms after typing stops (and on blur /
// Enter / menu close), only when they hold a valid number, clamped to the
// same minimums as before (MenuNumber). Same onUpdate -> scoped setting, so
// persistence is unchanged.
//
// SCOPE NOTE: the color pickers drive the COLUMNS only. The sigma bands keep
// their own posCol/negCol for now — deliberate, per spec.
// ============================================

const MODES = [
  { value: 'perbar',  label: 'Per-bar' },
  { value: 'rolling', label: 'Rolling' },
]
const UNITS = [
  { value: 'coins', label: 'Coins' },
  { value: 'usd',   label: 'Notional' },
]
const COLOR_MODES = [
  { value: 'fixed',   label: 'Fixed' },
  { value: 'dynamic', label: 'Dynamic σ' },
]

export default function DVPSettingsMenu({ show, x, y, openLeft, config, onUpdate, onHide, onClose }) {
  const [colPos, setColPos] = useState(config?.colPos || '#64ffda')
  const [colNeg, setColNeg] = useState(config?.colNeg || '#f44336')
  const [deltaMode, setDeltaMode] = useState(config?.deltaMode || 'perbar')
  const [unitMode, setUnitMode] = useState(config?.unitMode || 'coins')
  const [colorMode, setColorMode] = useState(config?.colorMode || 'fixed')
  const [rollingLen, setRollingLen] = useState(config?.rollingLen ?? 50)
  const [smoothLen, setSmoothLen] = useState(config?.smoothLen ?? 0)
  const [volLookback, setVolLookback] = useState(config?.volLookback ?? 50)
  const [showStddev, setShowStddev] = useState(config?.showStddev ?? true)
  const [fillBands, setFillBands] = useState(config?.fillBands ?? true)

  // Re-seed from config when the menu OPENS only (unchanged).
  const wasShownRef = useRef(false)
  useEffect(() => {
    if (show && config && !wasShownRef.current) {
      setColPos(config.colPos); setColNeg(config.colNeg)
      setDeltaMode(config.deltaMode); setUnitMode(config.unitMode)
      setColorMode(config.colorMode); setRollingLen(config.rollingLen)
      setSmoothLen(config.smoothLen); setVolLookback(config.volLookback)
      setShowStddev(config.showStddev); setFillBands(config.fillBands)
    }
    wasShownRef.current = !!show
  }, [show, config])

  // Instant commit for toggles / colours / selects.
  const commit = (patch) => onUpdate(patch)
  // Number fields: MenuNumber debounces (200ms) and clamps to min.
  const num = (key, set) => (n) => { set(n); commit({ [key]: n }) }

  if (!show) return null

  return (
    <ChartMenu anchor={{ x, y }} openLeft={openLeft} onClose={onClose}>
      {/* Row 1 — columns' bull/bear + mode + units */}
      <MenuRow>
        <MenuSwatch label="BULL" value={colPos} onChange={c => { setColPos(c); commit({ colPos: c }) }} />
        <MenuDivider />
        <MenuSwatch label="BEAR" value={colNeg} onChange={c => { setColNeg(c); commit({ colNeg: c }) }} />
        <MenuDivider />
        <MenuSelect value={deltaMode} options={MODES}
          onChange={v => { setDeltaMode(v); commit({ deltaMode: v }) }} />
        <MenuDivider />
        <MenuSelect value={unitMode} options={UNITS}
          onChange={v => { setUnitMode(v); commit({ unitMode: v }) }} />
        <MenuDivider />
        <MenuSelect value={colorMode} options={COLOR_MODES}
          onChange={v => { setColorMode(v); commit({ colorMode: v }) }} />
      </MenuRow>

      {/* Row 2 — the maths */}
      <MenuRow>
        {deltaMode === 'rolling' && (
          <>
            <MenuNumber label="LEN" value={rollingLen} min={2} max={500} width={44}
              onCommit={num('rollingLen', setRollingLen)} />
            <MenuDivider />
          </>
        )}
        <MenuNumber label="SMOOTH" value={smoothLen} min={0} max={200} width={44}
          onCommit={num('smoothLen', setSmoothLen)} />
        <MenuDivider />
        <MenuNumber label="σ LOOK" value={volLookback} min={5} max={500} width={44}
          onCommit={num('volLookback', setVolLookback)} />
      </MenuRow>

      {/* Row 3 — band toggles */}
      <MenuRow>
        <MenuToggle on={showStddev} onChange={v => { setShowStddev(v); commit({ showStddev: v }) }}>σ BANDS</MenuToggle>
        <MenuToggle on={fillBands} disabled={!showStddev}
          onChange={v => { setFillBands(v); commit({ fillBands: v }) }}>FILL</MenuToggle>
      </MenuRow>

      <MenuActions>
        <MenuButton variant="danger" onClick={() => { onHide(); onClose() }}>HIDE</MenuButton>
      </MenuActions>
    </ChartMenu>
  )
}
